import { newPostBannerShownAtom, userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page, usePagePadding } from 'app/design/primitives/page';
import { RemoteError } from 'app/design/primitives/remote-error';
import { Text } from 'app/design/primitives/text';

import { useForm } from 'app/hooks/form';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useAddListingPictures } from 'app/network/listing';
import { useOneLoan } from 'app/network/loan';
import { useCreatePost } from 'app/network/post';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useAtom, useAtomValue } from 'jotai';
import React, { type ComponentProps } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { PostComposer, PostComposerAction } from './components/post-composer';
import { StoryPostComposer, StoryPostComposerAction } from './components/story-post-composer';

interface NewPostFormValues {
  text: string;
  pictures: ImagePickerAsset[];
}

const newPostSchema: yup.ObjectSchema<NewPostFormValues> = yup.object({
  text: yup.string().trim().required(),
  pictures: yup.array().of(yup.mixed<ImagePickerAsset>().required()).defined(),
});

export function NewPostContent({
  navigation,
  route,
  embedded = false,
  backInHeader = false,
  onPosted,
  onBack,
}: ComponentProps<NavigationScreen<'new-post'>> & {
  embedded?: boolean;
  backInHeader?: boolean;
  onPosted?: () => void;
  onBack?: () => void;
}) {
  const { loanId, listingId } = route.params;
  const pagePadding = usePagePadding();
  const { t } = useTranslation('posts');
  const { canUseSocialFeed, isSocialFeedLoading } = useSocialFeedFeature();

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t('newPost.title')}
        </Text>
      ),
    },
    embedded,
  );

  const { mutate: addPictures } = useAddListingPictures(listingId);
  const {
    mutate: createPost,
    isPending,
    error,
  } = useCreatePost({
    onSuccess: () => (onPosted ? onPosted() : navigation.goBack()),
  });
  const { data: loan } = useOneLoan(loanId);
  const currentUser = useAtomValue(userInfoAtom);

  const {
    handleSubmit: handleFormSubmit,
    setValue,
    control,
  } = useForm<NewPostFormValues>({
    defaultValues: { text: '', pictures: [] },
    schema: newPostSchema,
  });
  const text = useWatch({ control, name: 'text' });
  const pictures = useWatch({ control, name: 'pictures' });
  const hasText = text.trim().length > 0;
  const canCreatePost = Boolean(loan && loan.borrowerId === currentUser?.id && !loan.posts?.length);

  React.useEffect(() => {
    if (!isSocialFeedLoading && !canUseSocialFeed) navigation.goBack();
  }, [canUseSocialFeed, isSocialFeedLoading, navigation]);

  const handleSubmit = (values: NewPostFormValues) => {
    if (!canUseSocialFeed) return;

    if (values.pictures.length > 0) {
      addPictures(values.pictures);
    }

    createPost({
      loanId,
      text: values.text.trim(),
      pictures: values.pictures,
    });
  };

  const [newPostBannerShown, setNewPostBannerShown] = useAtom(newPostBannerShownAtom);

  if (isSocialFeedLoading || !canUseSocialFeed) return null;

  return (
    <Page
      paddingStyle={embedded ? { ...pagePadding, paddingTop: backInHeader ? 12 : 16 } : undefined}
      accessory={
        canCreatePost ? (
          <NewPostAction
            embedded={embedded}
            disabled={!hasText || isPending}
            loading={isPending}
            onPress={() => void handleFormSubmit(handleSubmit)()}
          />
        ) : undefined
      }
      keyboardShouldPersistTaps="handled"
      stretchToFill
    >
      {error ? <RemoteError error={error} /> : null}
      {!loan || !currentUser ? (
        <Column alignItems="center" flex={1} justifyContent="center" padding="spacing.general.x8">
          <Text color="text.secondary">{t('newPost.loading')}</Text>
        </Column>
      ) : !canCreatePost ? (
        <Column
          alignItems="center"
          flex={1}
          gap="spacing.general.x3"
          justifyContent="center"
          padding="spacing.general.x8"
        >
          <Text fontWeight="700" textAlign="center" variant="bodyLarge">
            {t('newPost.unavailableTitle')}
          </Text>
          <Text color="text.secondary" textAlign="center" variant="bodyBase">
            {t('newPost.unavailableText')}
          </Text>
          <Button onPress={onBack ?? (() => navigation.goBack())} variant="secondary">
            <Text fontWeight="700">{t('newPost.goBack')}</Text>
          </Button>
        </Column>
      ) : (
        <NewPostComposer
          embedded={embedded}
          loan={loan}
          onChangeLoan={onBack}
          backInHeader={backInHeader}
          disabled={isPending}
          onDismissPublicNotice={() => setNewPostBannerShown(true)}
          onPicturesChange={(nextPictures) => setValue('pictures', nextPictures)}
          onTextChange={(value) => setValue('text', value)}
          pictures={pictures}
          showPublicNotice={!newPostBannerShown}
          text={text}
        />
      )}
    </Page>
  );
}

function NewPostComposer({ embedded, ...props }: ComponentProps<typeof StoryPostComposer> & { embedded: boolean }) {
  return embedded ? <StoryPostComposer {...props} /> : <PostComposer {...props} />;
}

function NewPostAction({
  embedded,
  ...props
}: Omit<ComponentProps<typeof PostComposerAction>, 'label'> & { embedded: boolean }) {
  const { t } = useTranslation('posts');
  const Action = embedded ? StoryPostComposerAction : PostComposerAction;
  return <Action {...props} label={t(embedded ? 'storyComposer.submitButton' : 'newPost.submitButton')} />;
}

const NewPostWithBoundary = withErrorBoundary(NewPostContent);

export function NewPostScreen(props: ComponentProps<NavigationScreen<'new-post'>>) {
  return <NewPostWithBoundary {...props} />;
}
